import React, { useContext } from 'react'
import { useFormikContext } from 'formik'
import { t } from '../../../i18n'
import { LocalizationContext } from '../../shared/contexts'
import { weightDecimals, weightStep } from '../../shared/weightScale'
import { useCatalogue } from '../CatalogueProvider'
import { FeederSubtype, FeederType } from '../catalogueTypes'
import { allCategoryOptions } from './categoryFormValues'
import { ProductFormValues, orderProviderFieldName, orderProviderLabel } from './productFormValues'
import { CatalogueFormSection } from './CatalogueFormControls'
import {
  CatalogueImageAndNameInput,
  ProductModifierRadio,
  CatalogueMultiSelect,
  CategoryPicker,
  CatalogueRadioGroup,
  CatalogueSelect,
  CatalogueStepper,
  CatalogueTextAreaInput,
  CatalogueTextInput
} from './CatalogueFormInputs'

const scope = 'product'

const FEEDER_TYPES: ReadonlyArray<readonly [FeederType, string]> = [
  ['liquid', t('catalogue.feeder_types.liquid')],
  ['solid', t('catalogue.feeder_types.solid')]
]

const FEEDER_SUBTYPES: ReadonlyArray<readonly [FeederSubtype, string]> = [
  ['fridge', t('feeder_subtypes.fridge', { scope })],
  ['standard', t('feeder_subtypes.standard', { scope })]
]

const VOLUME_MULTIPLIER_MAX_LENGTH = 5
const VOLUME_MULTIPLIER_STEP = 0.1

const DOSING_WEIGHT_STEP_GRAM_STEP = 5
const ALERT_GRAM_STEP = 5
const MAX_CONTAINER_AMOUNT_GRAM_STEP = 50
const MAX_CONTAINER_AMOUNT_IMPERIAL_STEP = 0.5

const OrderProviderFields = () => {
  const { meta } = useCatalogue()
  if (meta.orderProviders.length === 0) return null

  return (
    <div className="order-providers">
      {meta.orderProviders.map((provider) => (
        <CatalogueTextInput key={orderProviderFieldName(provider)} name={orderProviderFieldName(provider)} label={orderProviderLabel(provider)} />
      ))}
    </div>
  )
}

const ProductFormSections = ({ modifierBadge }: { modifierBadge?: React.ReactNode }) => {
  const { state, meta } = useCatalogue()
  const { values, setFieldValue } = useFormikContext<ProductFormValues>()
  const { weight_scale } = useContext(LocalizationContext)
  const { productAttributes: attributes } = state
  const unit = meta.weightUnit
  const weightInputDecimals = weightDecimals(weight_scale)
  const maxContainerAmountStep = weight_scale === 'imperial' ? MAX_CONTAINER_AMOUNT_IMPERIAL_STEP : MAX_CONTAINER_AMOUNT_GRAM_STEP
  const liquid = values.feeder_type === 'liquid'

  return (
    <>
      <CatalogueFormSection title={t('catalogue.sections.basic_info')} accent="blue">
        <CategoryPicker name="location" label={t('category')} options={allCategoryOptions(state.categoryOptions)} />
        <CatalogueImageAndNameInput placeholder={t('product_name', { scope })} />
      </CatalogueFormSection>
      <CatalogueFormSection title={t('catalogue.sections.feeder_configuration')} accent="green">
        <div className="form-section-rows">
          <CatalogueRadioGroup
            name="feeder_type"
            label={t('feeder_type', { scope })}
            options={FEEDER_TYPES}
            onValueChange={(value) => {
              if (value === 'liquid') setFieldValue('feeder_subtype', null)
            }}
          />
          {values.feeder_type === 'solid' ? (
            <CatalogueRadioGroup name="feeder_subtype" label={t('feeder_subtype', { scope })} options={FEEDER_SUBTYPES} />
          ) : null}
          <CatalogueSelect name="fraction_id" label={t('fraction', { scope })} options={attributes.fractions} />
          <CatalogueSelect name="unit_id" label={t('unit', { scope })} options={attributes.units} />
        </div>
      </CatalogueFormSection>
      <CatalogueFormSection title={t('catalogue.sections.dosing_settings')} accent="orange">
        <div className="form-section-columns">
          {liquid ? (
            <CatalogueSelect name="sauce_dosing_mode_id" label={t('sauce_dosing_mode', { scope })} options={attributes.sauceDosingModes} />
          ) : (
            <CatalogueSelect name="feeder_dosing_mode_id" label={t('feeder_dosing_mode', { scope })} options={attributes.feederDosingModes} />
          )}
          <CatalogueStepper
            name="dosing_weight_step"
            label={t('dosing_weight_step', { scope })}
            tooltip={t('dosing_weight_step_info', { scope })}
            unit={unit}
            step={weightStep(DOSING_WEIGHT_STEP_GRAM_STEP, weight_scale)}
            decimals={weightInputDecimals}
          />
          <CatalogueStepper name="dosing_time" label={t('dosing_time', { scope })} />
          {liquid ? (
            <CatalogueStepper
              name="volume_to_weight_multiplier"
              label={t(unit === 'g' ? 'conversion_to_liters' : 'conversion_to_fluid_ounces', { scope })}
              tooltip={unit === 'g' ? '0.1 kg => ? l' : '1 oz => ? fl-oz'}
              maxLength={VOLUME_MULTIPLIER_MAX_LENGTH}
              step={VOLUME_MULTIPLIER_STEP}
            />
          ) : null}
          <CatalogueStepper
            name="alert"
            label={t('alert', { scope })}
            unit={unit}
            step={weightStep(ALERT_GRAM_STEP, weight_scale)}
            decimals={weightInputDecimals}
          />
          <CatalogueStepper
            name="max_container_amount"
            label={t('max_container_amount', { scope })}
            unit={unit}
            step={maxContainerAmountStep}
            decimals={weightInputDecimals}
          />
        </div>
      </CatalogueFormSection>
      <CatalogueFormSection title={t('catalogue.sections.nutrition_and_allergens')} accent="green">
        <div className="form-section-rows">
          <CatalogueMultiSelect name="alergen_ids" label={t('alergens', { scope })} options={attributes.alergens} />
        </div>
        <div className="form-section-columns">
          <CatalogueStepper name="kcal" label={t('kcal', { scope })} />
          <CatalogueStepper name="kjoul" label={t('kj', { scope })} />
          <CatalogueStepper name="processing_loss" label={t('processing_loss', { scope })} unit="%" />
        </div>
      </CatalogueFormSection>
      <CatalogueFormSection title={t('catalogue.sections.modifier_and_integration')} accent="purple">
        <div className="form-section-rows">
          <ProductModifierRadio name="is_modifier">{modifierBadge}</ProductModifierRadio>
          {values.is_modifier ? <OrderProviderFields /> : null}
          <CatalogueTextAreaInput name="notes" label={t('notes', { scope })} />
        </div>
      </CatalogueFormSection>
    </>
  )
}

export default ProductFormSections
